import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, ArrowLeft } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="admin-login-page">
      <div className="hidden md:flex md:w-1/2 relative" style={{ backgroundColor: "#0A1128" }}>
        <div className="absolute inset-0 paper-grain opacity-30" />
        <div className="relative m-auto max-w-md p-12">
          <div className="flex items-center gap-3 mb-10">
            <span
              className="inline-flex items-center justify-center"
              style={{ width: 40, height: 40, border: "1px solid #C5A059", color: "#C5A059" }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3v18" />
                <path d="M5 21h14" />
                <path d="M5 7h14" />
                <path d="M5 7l-2 6a4 4 0 0 0 8 0L9 7" />
                <path d="M19 7l-2 6a4 4 0 0 0 8 0l-2-6" />
              </svg>
            </span>
            <div className="flex flex-col leading-tight">
              <span className="font-serif-display text-xl" style={{ color: "#F9F6F0", letterSpacing: "0.04em" }}>
                Sikdar &amp; Sikdar
              </span>
              <span style={{ color: "#C5A059", fontSize: "10px", letterSpacing: "0.32em", textTransform: "uppercase" }}>
                Advocates
              </span>
            </div>
          </div>
          <h1 className="font-serif-display text-5xl" style={{ color: "#F9F6F0", fontWeight: 500, lineHeight: 1.05 }}>
            Chamber
            <br />
            <span style={{ fontStyle: "italic", color: "#C5A059" }}>administration.</span>
          </h1>
          <p className="mt-8" style={{ color: "rgba(249,246,240,0.7)" }}>
            Review consultation enquiries received via the website and manage their
            status from this private dashboard.
          </p>
        </div>
      </div>

      <div className="flex-1 flex flex-col" style={{ backgroundColor: "#F9F6F0" }}>
        <div className="container-x pt-8">
          <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase" style={{ letterSpacing: "0.2em", color: "#0A1128" }} data-testid="back-to-site">
            <ArrowLeft size={14} strokeWidth={1.5} /> Back to website
          </Link>
        </div>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md mx-auto px-6 md:px-12 py-16">
            <Lock size={22} strokeWidth={1.5} style={{ color: "#7A2021" }} />
            <h2 className="font-serif-display text-4xl mt-6" style={{ color: "#0A1128", fontWeight: 500 }}>
              Administrator Login
            </h2>
            <p className="mt-3 text-sm" style={{ color: "#334155" }}>
              Authorised access only.
            </p>

            <form onSubmit={submit} className="mt-10 space-y-8" data-testid="admin-login-form">
              <label className="block">
                <span className="field-label">Email</span>
                <input
                  type="email"
                  className="field-input-box"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="admin-login-email"
                />
              </label>
              <label className="block">
                <span className="field-label">Password</span>
                <input
                  type="password"
                  className="field-input-box"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="admin-login-password"
                />
              </label>

              {error && (
                <div className="text-sm" style={{ color: "#991B1B" }} data-testid="admin-login-error">
                  {error}
                </div>
              )}

              <button type="submit" className="btn-primary w-full" disabled={loading} data-testid="admin-login-submit">
                {loading ? "Authenticating…" : "Sign In"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
